/* istanbul ignore file */
import { Button as MaterialButton } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import { rem, Theme, px } from '../../../theme';

import {
    StyledButtonProperties,
    LegacyStyle,
    RebrandStyle,
    RebrandStyleColors,
} from './Button.types';

const styleMap = (buttonStyle: LegacyStyle, theme: Theme) => {
    const { primary, secondary, success, common } = theme.palette;

    const colors = {
        primary: {
            main: primary.main,
            active: primary.dark,
        },
        // TODO: primary-hot-pink needs to be removed from CMS as well anything relating to primary-hot-pink
        'primary-hot-pink': {
            main: secondary.main,
            active: common.blue,
        },
        'primary-alternate': {
            main: secondary.main,
            active: common.blue,
        },
        'primary-green': {
            main: success.main,
            active: success.dark,
        },
        'primary-navy': {
            main: common.blue,
            active: common.blue,
        },
        secondary: {
            main: primary.main,
            active: primary.dark,
        },
        'secondary-white': {
            main: common.white,
            active: common.blue,
        },
        'secondary-blue-white': {
            main: common.blue,
            active: common.white,
        },
        tertiary: {
            main: primary.main,
            active: primary.dark,
        },
        pino: {
            main: common.white,
            active: success.main,
        },
    };

    return colors[buttonStyle];
};

const rebrandStyleMap = (
    buttonStyle: RebrandStyle,
    theme: Theme,
): RebrandStyleColors => {
    const pallete = theme.palette.tgg;

    const colors = {
        primary: {
            color: {
                main: pallete.text.on.action.main,
                disable: pallete.text.action.disabled,
            },
            background: {
                main: pallete.surface.action.main,
                focus: pallete.surface.action.main,
                hover: pallete.surface.action.hover,
                active: pallete.surface.action.pressed,
                disable: pallete.surface.action.disabled,
            },
            border: {
                focus: pallete.border.focus.main,
            },
        },
        'primary-inverse': {
            color: {
                main: pallete.text.on.action.inverse,
                disable: pallete.text.action.disabled,
            },
            background: {
                main: pallete.surface.action.inverse.main,
                focus: pallete.surface.action.inverse.main,
                hover: pallete.surface.action.inverse.hover,
                active: pallete.surface.action.inverse.pressed,
                disable: pallete.surface.action.disabled,
            },
            border: {
                focus: pallete.border.focus.inverse,
            },
        },
        secondary: {
            color: {
                main: pallete.text.on.action.secondary,
                disable: pallete.text.action.disabled,
            },
            background: {
                main: pallete.surface.action.secondary.main,
                focus: pallete.surface.action.secondary.main,
                hover: pallete.surface.action.secondary.hover,
                active: pallete.surface.action.secondary.pressed,
                disable: pallete.surface.action.disabled,
            },
            border: {
                main: pallete.border.action.main,
                focus: pallete.border.action.main,
                disable: pallete.border.disabled,
            },
        },
        'secondary-inverse': {
            color: {
                main: pallete.text.on.action.secondaryInverse,
                disable: pallete.text.action.inverse.disabled,
            },
            background: {
                main: alpha(
                    theme.palette.tgg.surface.action.secondaryInverse.main,
                    0.4,
                ),
                focus: alpha(
                    theme.palette.tgg.surface.action.secondaryInverse.main,
                    0.4,
                ),
                hover: pallete.surface.action.secondaryInverse.hover,
                active: pallete.surface.action.secondaryInverse.pressed,
                disable: alpha(
                    theme.palette.tgg.surface.action.secondaryInverse.main,
                    0.4,
                ),
            },
            border: {
                main: pallete.border.action.secondaryInverse,
                focus: pallete.border.action.secondaryInverse,
                disable: pallete.border.inverse.disabled,
            },
        },
        'secondary-tonal': {
            color: {
                main: pallete.text.on.action.secondaryTonal,
                disable: pallete.text.action.disabled,
            },
            background: {
                main: pallete.surface.action.secondaryTonal.main,
                focus: pallete.surface.action.secondaryTonal.main,
                hover: pallete.surface.action.secondaryTonal.hover,
                active: pallete.surface.action.secondaryTonal.pressed,
                disable: pallete.surface.action.disabled,
            },
            border: {
                focus: pallete.border.focus.main,
            },
        },
        destructive: {
            color: {
                main: pallete.text.on.action.main,
                disable: pallete.text.action.disabled,
            },
            background: {
                main: pallete.surface.action.destructive.main,
                focus: pallete.surface.action.destructive.main,
                hover: pallete.surface.action.destructive.hover,
                active: pallete.surface.action.destructive.pressed,
                disable: pallete.surface.action.disabled,
            },
            border: {
                focus: pallete.border.focus.main,
            },
        },
        tertiary: {
            color: {
                main: pallete.text.action.main,
                disable: pallete.text.action.disabled,
            },
            background: {
                main: 'transparent',
                focus: 'transparent',
                hover: pallete.surface.action.tertiary.hover,
                active: pallete.surface.action.tertiary.pressed,
                disable: 'transparent',
            },
            border: {
                focus: pallete.border.focus.main,
            },
        },
    };

    return colors[buttonStyle];
};

const StyledMaterialButton = styled(MaterialButton)<StyledButtonProperties>`
    ${({ theme }: { theme: Theme }) =>
        theme.isRebranding(
            theme.typography.text_19_bold,
            theme.typography.buttonLabelLargeRebrand,
        )};
    ${({ theme }: StyledButtonProperties & { theme: Theme }) => `
        /* stylelint-disable unit-allowed-list */
        padding: ${theme.tggSpacing([rem(0.55), rem(1.4)], [3, 4])};
        text-align: center;
        text-transform: ${theme.isRebranding('uppercase', 'unset')};
        border-radius: ${theme.tggSpacing(rem(3.2), 250)};
	`}
`;

export const StyledRebrandedButton = styled(StyledMaterialButton, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledButtonProperties>`
    ${({ $buttonStyle, theme }) => {
        const { color, background, border } = rebrandStyleMap(
            $buttonStyle as RebrandStyle,
            theme,
        );
        return `
            color: ${color.main};
            background-color: ${background.main};
            border: ${border.main ? `${theme.tggSpacing(rem(0.25), 0.5)} solid ${border.main}` : 'none'};

            &.MuiButtonBase-root:focus-visible,
            &.MuiButtonBase-root:focus {
                background-color: ${background.focus};
                outline: ${theme.tggSpacing(rem(0.25), 0.5)} solid ${border.focus};
                outline-offset: ${theme.tggSpacing(rem(0.25), 0.5)};
            }

            &.MuiButton-root:hover {
                background-color: ${background.hover};
            }
            &.MuiButton-root:active {
                background-color: ${background.active};
            }
            &.MuiButton-root.Mui-disabled {
                color: ${color.disable};
                border: ${border.disable ? `${theme.tggSpacing(rem(0.25), 0.5)} solid ${border.disable}` : 'none'};
                background-color: ${background.disable};
            }
        `;
    }}
`;

export const StyledPrimaryButton = styled(StyledMaterialButton, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledButtonProperties>`
    ${({ $buttonStyle, theme }) => {
        const color = styleMap($buttonStyle as LegacyStyle, theme);
        return `
            text-transform: lowercase;
            color: ${
                color.main === theme.palette.secondary.main
                    ? color.active
                    : theme.palette.common.white
            };
            background-color: ${color.main};
            &.MuiButton-root:focus {
                color: ${
                    color.main === theme.palette.secondary.main
                        ? color.main
                        : theme.palette.common.white
                };

                outline: ${theme.tggSpacing(rem(0.25), 0.5)} solid ${color.active};
                outline-offset: ${theme.tggSpacing(rem(0.25), 0.5)};
            }

            &.MuiButton-root:hover {
                color: ${
                    color.main === theme.palette.secondary.main
                        ? color.main
                        : theme.palette.common.white
                };
                background-color: ${color.active};
            }

            &.MuiButton-root.Mui-disabled {
                color: ${
                    color.main === theme.palette.secondary.main
                        ? color.active
                        : theme.palette.common.white
                };
                background-color:
                    ${alpha(color.main, 0.5)};
                border-color: ${color.main};
            }
        `;
    }}
`;

export const SecondaryStyledButton = styled(StyledMaterialButton, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledButtonProperties>`
    ${({ theme, $buttonStyle }) => {
        const color = styleMap($buttonStyle as LegacyStyle, theme);
        return `
            text-transform: lowercase;
            color: ${color.main};
            background-color: ${
                color.main === theme.palette.common.white
                    ? 'transparent'
                    : theme.palette.common.white
            };
            border: ${theme.tggSpacing(rem(0.1), 0.25)} solid ${color.main};

            &.MuiButton-root:focus {
                outline: ${theme.tggSpacing(rem(0.25), 0.5)} solid ${color.active};
                outline-offset: ${theme.tggSpacing(rem(0.25), 0.5)}
            }

            &.MuiButton-root:hover {
                color: ${
                    color.main === theme.palette.common.white
                        ? theme.palette.common.blue
                        : theme.palette.common.white
                };
                background-color: ${color.main};
                border-color: ${color.main};
            }

            &.MuiButton-root.Mui-disabled {
                color: ${alpha(color.main, 0.5)};
                background-color: ${
                    color.main === theme.palette.common.white
                        ? 'transparent'
                        : alpha(theme.palette.common.white, 0.5)
                };
                border-color: ${color.main};
            }
        `;
    }}
`;

export const StyledTertiaryButton = styled(StyledMaterialButton, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledButtonProperties>`
    ${({ $buttonStyle, theme }) => {
        const color = styleMap($buttonStyle as LegacyStyle, theme);

        return `
            color: ${color.main};
            text-transform: none;
            background-color: transparent;

            &:hover {
                color: ${theme.palette.common.white};
            }

            &.MuiButton-root.Mui-disabled {
                color: ${alpha(color.main, 0.5)};
            }
        `;
    }}
`;

export const StyledSmallButton = styled(StyledMaterialButton, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledButtonProperties>`
    ${({ theme }) => theme.typography.bodyXSmall};
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing([rem(0.45), rem(1.5)], [1, 3.75])};
        font-weight: 500;
        text-transform: lowercase;
        background-color: ${theme.palette.common.white};
        border: ${theme.tggSpacing(px(1), 0.25)}
            solid;
        border-radius: ${theme.tggSpacing(rem(3.2), 12)};
        

        &.MuiButton-root:focus {
            outline: ${theme.tggSpacing(rem(0.25), 0.625)}
                solid ${theme.palette.common.blue};
            outline-offset: ${theme.tggSpacing(rem(0.25), 0.625)};
        }

        &.MuiButton-root:hover {
            color: ${theme.palette.common.white};
            background-color: ${theme.palette.common.blue};
            border: ${theme.tggSpacing(px(1), 0.25)}
                solid transparent;
        }
	`}
`;
